/**
 * <s-sticky-atc> — Sticky add-to-cart bar for the product page.
 *
 * Shows once the main product form scrolls out of view and hides again when it
 * returns. The bar's button submits the real product form, so variant, quantity
 * and cart drawer behaviour all stay owned by <s-product-form>.
 *
 * Commands (via commandfor/command on a descendant button):
 *   --add — Submits the main form with its add button; scrolls to the form when sold out.
 *
 * Attributes:
 *   data-watch — CSS selector for the main product form region to observe.
 *
 * Child markup:
 *   [data-sticky-price] — Receives a copy of the current [data-product-price] text.
 */
class SStickyAtc extends HTMLElement {
  #ac;
  #observer;
  #watched;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#watched = document.querySelector(this.dataset.watch || '[data-product-form]');
    this.#setVisible(false);
    if (!this.#watched) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.#setVisible(passed);
    });
    this.#observer.observe(this.#watched);

    this.addEventListener('command', (event) => {
      if (event.command !== '--add') return;
      this.#add();
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #setVisible(visible) {
    if (visible) {
      this.#syncPrice();
      this.removeAttribute('inert');
      this.removeAttribute('aria-hidden');
      this.dataset.visible = '';
    } else {
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      delete this.dataset.visible;
    }
  }

  #syncPrice() {
    const source = document.querySelector('[data-product-price]');
    const target = this.querySelector('[data-sticky-price]');
    if (source && target) target.textContent = source.textContent.replace(/\s+/g, ' ').trim();
  }

  #add() {
    const button = this.#watched?.querySelector('button[name="add"]');
    const form = button?.form;
    if (form && button.getAttribute('aria-disabled') !== 'true') {
      form.requestSubmit(button);
      return;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#watched?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
